import { useState, useEffect, useContext } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormCheck,
    SttFormPassword,
    SttFormText,
    SttFormSelect,
} from '../../sporttia/all';
import LogoHeading from '../../layout/LogoHeading';
import { useQuery, useLoader } from '../../lib/hooks';

const lang = navigator.language;
let previousLang = '';

type RegisterParams = {
    name: string;
    surname1: string;
    surname2: string;
    email: string;
    password: string;
    gender: string;
};

type User = {
    id: number;
    name?: string;
    email?: string;
};

export default function Register(): React.ReactElement {
    const { siteKey: recaptchaSiteKey } = (window.runtimeConfig as any)
        .reCaptcha;

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { redirect } = useQuery();
    const [registerStatus, setRegisterStatus] = useState<'idle' | 'loading'>(
        'idle',
    );
    const [, loader] = useLoader([registerStatus]);
    const [captchaToken, setCaptchaValue] = useState<string | undefined>();
    const [captchaAction, setCaptchaAction] = useState<string | undefined>();
    const userLang = cxt?.lang ? cxt.lang.key : lang;

    // Params that will be sent to the API
    const [params, setParams] = useState<RegisterParams>({
        name: '',
        surname1: '',
        surname2: '',
        email: '',
        password: '',
        gender: 'UNKNOWN',
    });

    // Checkboxes for "I accept stuff" and "I'm older than 14"
    const [privacyChecked, setPrivacyChecked] = useState(false);
    const [olderThan14, setOlderThan14] = useState(false);

    // Set the the param 'name' to 'value'
    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (name) {
            setParams({ ...params, [name]: value as string });
        }
    }

    useEffect(() => {
        if (cxt?.config?.languages) {
            if (lang && lang !== previousLang) {
                const langCode = lang.split('-');

                const currentLang = cxt.config.languages.find(
                    (langKey) => langKey.key === langCode[0],
                );

                if (currentLang) {
                    cxt.setLang(currentLang);
                }

                previousLang = lang;
            } else {
                localStorage.removeItem('currentLanguage');
            }
        }
    }, [cxt]);

    // Do call the API. Must've checked both checkboxes
    function register() {
        if (!privacyChecked || !olderThan14 || !captchaToken) {
            cxt?.showMessage('E', t('register.mustAcceptAll'));
        } else {
            setRegisterStatus('loading');
            cxt?.api('POST', `/users`, {
                params: {
                    ...params,
                    captchaAction,
                    captchaToken,
                    language: userLang,
                },
                success: (response: { user?: User }) => {
                    setRegisterStatus('idle');
                    if (response.user) {
                        // On success, we move to the 'email confirmation' screen, passing state: {user}
                        navigate(`/email/activation`, {
                            replace: true,
                            state: { user: response.user },
                        });
                    }
                },
                error: (response: unknown) => {
                    setRegisterStatus('idle');
                    if (
                        typeof response === 'object' &&
                        response !== null &&
                        'error' in response
                    ) {
                        const errorResponse = response as {
                            error?: { msg: string };
                        };
                        if (errorResponse.error) {
                            cxt?.showMessage('E', errorResponse.error.msg);
                        }
                    }
                },
            });
        }
    }

    function onLanguangeChange(langOnChange: {
        name: string | undefined;
        value: unknown;
    }) {
        const currentLang = cxt?.config?.languages?.find(
            (post) => post.key === langOnChange.value,
        );

        if (currentLang) {
            cxt?.setLang(currentLang);
        }
    }

    /**
     * Logged user changes
     */
    useEffect(() => {
        if (cxt?.user?.id && redirect) {
            const redirectUrl = Array.isArray(redirect)
                ? redirect[0]
                : redirect;
            if (redirectUrl) {
                navigate(decodeURIComponent(redirectUrl));
            }
        }
    }, [cxt?.user, navigate, redirect]);

    // Render
    return (
        <>
            {loader}
            <Container maxWidth="sm">
                <Box mt={8}>
                    <Paper>
                        <Box p={8}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <LogoHeading
                                        caption={t(
                                            'register.title',
                                        ).toUpperCase()}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormSelect
                                        name="Language"
                                        caption={t('general.language')}
                                        defVal={userLang}
                                        options={
                                            cxt?.config?.languages
                                                ? [...cxt.config.languages]
                                                : []
                                        }
                                        onChange={onLanguangeChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        name="name"
                                        caption={t('labels.name')}
                                        defVal={params.name}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        name="surname1"
                                        caption={t('labels.surname1')}
                                        defVal={params.surname1}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        name="surname2"
                                        caption={t('labels.surname2')}
                                        defVal={params.surname2}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        name="email"
                                        caption={t('labels.email')}
                                        defVal={params.email}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormPassword
                                        name="password"
                                        caption={t('user.password')}
                                        defVal={params.password}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <SttFormCheck
                                        caption={
                                            <span>
                                                {t('register.acceptTerms1')}{' '}
                                                <Link
                                                    href="/privacy"
                                                    underline="hover"
                                                    color="primary"
                                                >
                                                    {t(
                                                        'register.privacyPolicy',
                                                    )}
                                                </Link>
                                            </span>
                                        }
                                        checked={privacyChecked}
                                        onChange={({
                                            value,
                                        }: {
                                            name: string;
                                            value: unknown;
                                        }) =>
                                            setPrivacyChecked(value as boolean)
                                        }
                                    />
                                    <SttFormCheck
                                        caption={t('register.olderThan14')}
                                        checked={olderThan14}
                                        onChange={({
                                            value,
                                        }: {
                                            name: string;
                                            value: unknown;
                                        }) => setOlderThan14(value as boolean)}
                                    />
                                    <Box>
                                        <Typography variant="body2">
                                            {t('register.olderThan14Desc')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        startIcon={<HowToRegIcon />}
                                        onClick={() => register()}
                                    >
                                        {t('register.register')}
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Box
                                        pr={2}
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <Link
                                            href="/login"
                                            variant="body2"
                                            underline="hover"
                                            color="primary"
                                        >{`${t('register.alreadyHaveOne')} ${t(
                                            'register.loginHere',
                                        )}`}</Link>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Box
                                        textAlign="center"
                                        display="flex"
                                        justifyContent="center"
                                        mt={4}
                                    >
                                        <ReCAPTCHA
                                            sitekey={recaptchaSiteKey}
                                            onChange={(
                                                token: string | null,
                                            ) => {
                                                setCaptchaValue(
                                                    token || undefined,
                                                );
                                                setCaptchaAction('signup');
                                            }}
                                            badge="inline"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </>
    );
}
